const CART_KEY = 'videoed_booking_cart';

export function loadCart() {
  try {
    const saved = JSON.parse(localStorage.getItem(CART_KEY));
    if (saved && Array.isArray(saved.items)) {
      return saved;
    }
  } catch {}
  return { items: [], deliveryDate: '', collectionDate: '' };
}

export function saveCart(cart) {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
  window.dispatchEvent(new Event('storage'));
}

export function addToCart(itemOrSlug, deliveryDate, collectionDate) {
  const cart = loadCart();

  // Handle new object-based format (from CameraCatalogue, DroneHire, etc.)
  if (typeof itemOrSlug === 'object' && itemOrSlug !== null) {
    const item = itemOrSlug;
    const slug = item.slug || item.cameraSlug || '';

    // Enforce 21-day maximum hire period
    const itemDelivery = deliveryDate || cart.deliveryDate || '';
    const itemCollection = collectionDate || cart.collectionDate || '';
    if (itemDelivery && itemCollection) {
      const calendarDays = Math.ceil((new Date(itemCollection) - new Date(itemDelivery)) / 86400000);
      if (calendarDays > 21) {
        return { success: false, message: 'Hire period must be 21 days or less' };
      }
    }

    // Match by slug AND dates so different hire periods create separate line items
    const exists = cart.items.find(i => 
      (i.slug || i.cameraSlug) === slug &&
      (i.deliveryDate || '') === (itemDelivery) &&
      (i.collectionDate || '') === (itemCollection)
    );
    if (exists) {
      exists.qty = (exists.qty || 1) + 1;
    } else {
      cart.items.push({
        ...item,
        qty: item.quantity || 1,
        deliveryDate: deliveryDate || cart.deliveryDate,
        collectionDate: collectionDate || cart.collectionDate,
        addedAt: new Date().toISOString()
      });
    }
    // Store last-used dates at cart level for "continue shopping" links
    if (deliveryDate) cart.deliveryDate = deliveryDate;
    if (collectionDate) cart.collectionDate = collectionDate;
    saveCart(cart);
    return { success: true, message: 'Item added to cart' };
  }

  // Old string-based format (backwards compatible)
  const cameraSlug = itemOrSlug;
  const exists = cart.items.find(item => item.cameraSlug === cameraSlug);
  if (exists) {
    exists.qty = (exists.qty || 1) + 1;
    saveCart(cart);
    return { success: true, message: 'Quantity updated' };
  }

  cart.items.push({ cameraSlug, qty: 1, addedAt: new Date().toISOString() });
  cart.deliveryDate = deliveryDate || cart.deliveryDate;
  cart.collectionDate = collectionDate || cart.collectionDate;

  saveCart(cart);
  return { success: true, message: 'Camera added to cart' };
}

export function updateItemQuantity(cameraSlug, qty) {
  const cart = loadCart();
  const item = cart.items.find(item => item.cameraSlug === cameraSlug);
  if (item) {
    item.qty = Math.max(1, qty);
    saveCart(cart);
  }
}

export function removeFromCart(identifier) {
  const cart = loadCart();
  cart.items = cart.items.filter(item => 
    item.cameraSlug !== identifier && item.slug !== identifier && item.id !== identifier && item.crewType !== identifier
  );
  saveCart(cart);
}

export function clearCart() {
  saveCart({ items: [], deliveryDate: '', collectionDate: '' });
}

export function addCrewToCart(crewData) {
  const cart = loadCart();
  cart.items.push({
    type: 'crew',
    id: `crew-${Date.now()}`,
    ...crewData,
    qty: 1,
    addedAt: new Date().toISOString()
  });
  saveCart(cart);
  return { success: true, message: 'Crew added to cart' };
}

export function setSameDayCourier(courierData) {
  const cart = loadCart();
  cart.sameDayCourier = courierData;
  saveCart(cart);
  return { success: true };
}

export function getSameDayCourier() {
  const cart = loadCart();
  return cart.sameDayCourier || null;
}

export function getCartCount() {
  const cart = loadCart();
  return cart.items.reduce((sum, item) => sum + (item.qty || 1), 0);
}

export function setCartPencilIds(ids) {
  const cart = loadCart();
  cart.pencilBookingIds = ids;
  saveCart(cart);
}

export function getCartPencilIds() {
  const cart = loadCart();
  return cart.pencilBookingIds || [];
}

export function setCartCustomerInfo(info) {
  const cart = loadCart();
  cart.customerInfo = info;
  saveCart(cart);
}

export function getCartCustomerInfo() {
  const cart = loadCart();
  return cart.customerInfo || null;
}